import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ClipboardCheck, Search, Eye, Users, AlertTriangle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const STATUS_LABELS = { present: 'Presente', late: 'Tarde', absent: 'Ausente' };
const STATUS_COLORS = { present: 'bg-green-100 text-green-800', late: 'bg-amber-100 text-amber-800', absent: 'bg-red-100 text-red-800' };

const rateColor = (rate) => {
  if (rate >= 90) return 'bg-green-100 text-green-800';
  if (rate >= 75) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const AdminAttendanceTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState('all');
  const [search, setSearch] = useState('');
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => { fetchOverview(); }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/attendance/admin/overview');
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error('Error al cargar la asistencia');
    } finally {
      setLoading(false);
    }
  };

  const grades = useMemo(() => [...new Set(rows.map(r => r.assigned_grade))].sort(), [rows]);
  const students = useMemo(() => rows.map(r => [r.student_id, r.student_name]).sort((a, b) => a[1].localeCompare(b[1])), [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (selectedGrade !== 'all' && r.assigned_grade !== selectedGrade) return false;
      if (selectedStudent !== 'all' && r.student_id !== selectedStudent) return false;
      if (search.trim() && !r.student_name.toLowerCase().includes(search.trim().toLowerCase())) return false;
      return true;
    });
  }, [rows, selectedGrade, selectedStudent, search]);

  const stats = useMemo(() => {
    const totalPresent = filteredRows.reduce((s, r) => s + r.present, 0);
    const totalDays = filteredRows.reduce((s, r) => s + r.total, 0);
    const avgRate = totalDays > 0 ? (totalPresent / totalDays) * 100 : 0;
    const atRisk = filteredRows.filter(r => r.attendance_rate < 75).length;
    return { totalStudents: filteredRows.length, avgRate, atRisk };
  }, [filteredRows]);

  return (
    <div className="space-y-6" data-testid="admin-attendance-tab">
      <div>
        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-indigo-600" /> Asistencia
        </h2>
        <p className="text-slate-500">Ausencias y tardanzas de todos los estudiantes (últimos 90 días)</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="modern-card" data-testid="att-stat-students">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Estudiantes con registro</p>
              <p className="text-2xl font-bold text-slate-800">{stats.totalStudents}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
          </CardContent>
        </Card>
        <Card className="modern-card" data-testid="att-stat-rate">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Tasa de asistencia promedio</p>
              <p className="text-2xl font-bold text-slate-800">{stats.avgRate.toFixed(1)}%</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
          </CardContent>
        </Card>
        <Card className="modern-card" data-testid="att-stat-atrisk">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Estudiantes en riesgo</p>
              <p className="text-2xl font-bold text-slate-800">{stats.atRisk}</p>
              <p className="text-[11px] text-slate-400">Asistencia menor al 75%</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
          </CardContent>
        </Card>
      </div>

      <Card className="modern-card">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Buscar estudiante..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="att-search-input" />
            </div>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger className="w-full lg:w-48" data-testid="att-student-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estudiantes</SelectItem>
                {students.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedGrade} onValueChange={setSelectedGrade}>
              <SelectTrigger className="w-full lg:w-40" data-testid="att-grade-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los grados</SelectItem>
                {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="modern-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div></div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              No hay registros de asistencia que coincidan con los filtros seleccionados.
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estudiante</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Grado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Presente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tarde</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ausente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tasa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRows.map((r, i) => (
                      <tr key={r.student_id} className="hover:bg-gray-50" data-testid={`att-row-${i}`}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-800">{r.student_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.assigned_grade}</td>
                        <td className="px-4 py-3 text-sm text-green-700">{r.present}</td>
                        <td className="px-4 py-3 text-sm text-amber-700">{r.late}</td>
                        <td className="px-4 py-3 text-sm text-red-700">{r.absent}</td>
                        <td className="px-4 py-3"><Badge className={rateColor(r.attendance_rate)}>{r.attendance_rate}%</Badge></td>
                        <td className="px-4 py-3">
                          <button onClick={() => setDetailRow(r)} className="text-indigo-600 hover:text-indigo-800" data-testid={`att-view-detail-${i}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y divide-gray-200" data-testid="att-mobile-list">
                {filteredRows.map((r, i) => (
                  <div key={r.student_id} className="p-4 space-y-1.5 cursor-pointer" onClick={() => setDetailRow(r)} data-testid={`att-card-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">{r.student_name}</p>
                      <Badge className={rateColor(r.attendance_rate)}>{r.attendance_rate}%</Badge>
                    </div>
                    <p className="text-xs text-slate-500">{r.assigned_grade}</p>
                    <p className="text-xs text-slate-400">Presente: {r.present} · Tarde: {r.late} · Ausente: {r.absent}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detailRow} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent className="max-w-lg" data-testid="att-detail-dialog">
          {detailRow && (
            <>
              <DialogHeader>
                <DialogTitle>{detailRow.student_name} — {detailRow.assigned_grade}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 -mt-2">Tasa de asistencia: {detailRow.attendance_rate}% ({detailRow.present} presente, {detailRow.late} tarde, {detailRow.absent} ausente)</p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {detailRow.records.map((rec, idx) => (
                  <div key={rec.id || idx} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-50" data-testid={`att-detail-record-${idx}`}>
                    <div>
                      <p className="text-sm text-slate-800">{new Date(rec.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      {rec.notes && <p className="text-xs text-slate-500">{rec.notes}</p>}
                    </div>
                    <Badge className={STATUS_COLORS[rec.status] || 'bg-gray-100 text-gray-800'}>{STATUS_LABELS[rec.status] || rec.status}</Badge>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAttendanceTab;
